<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AuthenticatedVImg from '../AuthenticatedVImg.vue'
import GroupInviteJoinDialog from '../dialogs/GroupInviteJoinDialog.vue'
import {
  fetchMatrixUrlPreview,
  type MatrixUrlPreview,
} from '@/modules/chat-matrix-fast/services/matrixUrlPreview'
import { isGroupInviteUrl, parseGroupInviteMeta, GROUP_INVITE_UI_ENABLED } from '@/modules/chat-matrix-fast/utils/groupInviteLink'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { isGroupChat } from '@/modules/chat-matrix-fast/types/ChatMessage'

const props = defineProps<{
  url: string
}>()

const coreStore = useChatStoreCore()
const viewStore = useChatStore()

const preview = ref<MatrixUrlPreview | null>(null)
const loading = ref(false)
const showJoinDialog = ref(false)
const previewImage = computed(() => preview.value?.image || undefined)

const inviteMeta = computed(() => parseGroupInviteMeta(props.url))
const inviteChatId = computed(() => inviteMeta.value?.chatId ?? null)
// TODO: вернуть UI ссылок-приглашений (GROUP_INVITE_UI_ENABLED)
const isInvite = computed(() => GROUP_INVITE_UI_ENABLED && inviteChatId.value != null)

const inviteTitle = computed(() => {
  if (inviteMeta.value?.title?.trim()) {
    return inviteMeta.value.title.trim()
  }

  const chatId = inviteChatId.value
  if (!chatId) {
    return 'Группа'
  }

  if (coreStore.currentChat?.id === chatId) {
    return coreStore.currentChat.title?.trim() || 'Группа'
  }

  const fromList = coreStore.chats.find(chat => chat.id === chatId)
  if (fromList?.title?.trim()) {
    return fromList.title.trim()
  }

  const active = viewStore.activeChat?.chat
  if (active && isGroupChat(active) && active.id === chatId) {
    return active.title?.trim() || 'Группа'
  }

  return 'Группа'
})

const inviteDescription = computed(() => 'Приглашение вступить в группу')

async function loadOgPreview() {
  const url = props.url?.trim()
  if (!url) {
    preview.value = null
    return
  }

  loading.value = true
  try {
    preview.value = await fetchMatrixUrlPreview(url)
  } finally {
    loading.value = false
  }
}

async function load() {
  if (GROUP_INVITE_UI_ENABLED && isGroupInviteUrl(props.url)) {
    preview.value = null
    loading.value = false
    return
  }

  await loadOgPreview()
}

function openInvite() {
  if (!inviteChatId.value) {
    return
  }
  showJoinDialog.value = true
}

const sentinel = ref<HTMLElement | null>(null)
const loaded = ref(false)
let observer: IntersectionObserver | null = null

function disconnectObserver() {
  observer?.disconnect()
  observer = null
}

function loadOnce() {
  if (loaded.value) {
    return
  }
  loaded.value = true
  disconnectObserver()
  void load()
}

function observeSentinel() {
  disconnectObserver()
  if (loaded.value) {
    return
  }

  const el = sentinel.value
  if (!el || typeof IntersectionObserver === 'undefined') {
    loadOnce()
    return
  }

  observer = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting)) {
      loadOnce()
    }
  }, { rootMargin: '200px 0px' })
  observer.observe(el)
}

onMounted(observeSentinel)
onBeforeUnmount(disconnectObserver)
watch(() => props.url, async () => {
  loaded.value = false
  preview.value = null
  await nextTick()
  observeSentinel()
})
</script>

<template>
  <template v-if="isInvite">
    <button
      type="button"
      class="invite-preview"
      @click.stop.prevent="openInvite"
    >
      <div class="invite-preview__accent" />
      <div class="invite-preview__body">
        <div class="invite-preview__title">
          {{ inviteTitle }}
        </div>
        <div class="invite-preview__desc">
          {{ inviteDescription }}
        </div>
        <div class="invite-preview__action">
          Вступить в группу
        </div>
      </div>
    </button>

    <GroupInviteJoinDialog
      v-model="showJoinDialog"
      :chat-id="inviteChatId"
      :fallback-title="inviteTitle"
      :fallback-type="inviteMeta?.type"
    />
  </template>

  <a
    v-else-if="preview"
    class="link-preview"
    :href="preview.url"
    target="_blank"
    rel="noopener noreferrer"
  >
    <div
      v-if="previewImage"
      class="link-preview__image"
    >
      <AuthenticatedVImg
        :src="previewImage"
        alt=""
      />
    </div>
    <div class="link-preview__body">
      <div
        v-if="preview.siteName"
        class="link-preview__site"
      >
        {{ preview.siteName }}
      </div>
      <div class="link-preview__title">
        {{ preview.title || preview.url }}
      </div>
      <div
        v-if="preview.description"
        class="link-preview__desc"
      >
        {{ preview.description }}
      </div>
    </div>
  </a>
  <div
    v-else-if="loading"
    class="link-preview link-preview--loading"
  >
    Загрузка превью…
  </div>
  <span
    v-else-if="!loaded"
    ref="sentinel"
    class="link-preview__sentinel"
  />
</template>

<style lang="scss" scoped>
.invite-preview {
  display: flex;
  width: 100%;
  max-width: 360px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  text-align: left;
  color: inherit;
  cursor: pointer;
  overflow: hidden;
}

.invite-preview__accent {
  flex: 0 0 3px;
  width: 3px;
  background: rgb(var(--v-theme-primary));
}

.invite-preview__body {
  min-width: 0;
  flex: 1;
  padding: 8px 12px 0;
}

.invite-preview__title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.invite-preview__desc {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.3;
  color: rgba(var(--v-theme-on-surface), 0.7);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.invite-preview__action {
  margin-top: 8px;
  padding: 10px 0;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.link-preview {
  display: flex;
  gap: 10px;
  max-width: 360px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  text-decoration: none;
  color: inherit;
  overflow: hidden;
}

.link-preview--loading {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.link-preview__sentinel {
  display: block;
  width: 1px;
  height: 1px;
}

.link-preview__image {
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.link-preview__body {
  min-width: 0;
  flex: 1;
}

.link-preview__site {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 2px;
}

.link-preview__title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.link-preview__desc {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.3;
  color: rgba(var(--v-theme-on-surface), 0.65);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
